import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ViewingsDispatchRequest } from "@/schemas";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import {
  getInspectionTypeLabel,
  getLifecycleStatusLabel,
} from "../viewingLabels";

export type DistanceFilter = "under5" | "5to10" | "10to15" | "over15";
export type AlertFilter = "waitingOver48h";

export type InspectorFilterState = {
  statuses: ViewingsDispatchRequest["lifecycleStatus"][];
  inspectionTypes: NonNullable<ViewingsDispatchRequest["inspectionType"]>[];
  products: string[];
  distances: DistanceFilter[];
  alerts: AlertFilter[];
};

export const EMPTY_INSPECTOR_FILTERS: InspectorFilterState = {
  statuses: [],
  inspectionTypes: [],
  products: [],
  distances: [],
  alerts: [],
};

const DISTANCE_OPTIONS: DistanceFilter[] = [
  "under5",
  "5to10",
  "10to15",
  "over15",
];

export function getInspectorFilterCount(filters: InspectorFilterState) {
  return (
    filters.statuses.length +
    filters.inspectionTypes.length +
    filters.products.length +
    filters.distances.length +
    filters.alerts.length
  );
}

function getDistanceLabel(intl: IntlShape, distance: DistanceFilter) {
  return intl.formatMessage({ id: `viewings.filters.distance.${distance}` });
}

function toggleValue<T>(values: T[], value: T) {
  return values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];
}

export default function InspectorGroupedFilterPopover({
  filters,
  statusOptions,
  inspectionOptions,
  productOptions,
  onApply,
  onClear,
}: {
  filters: InspectorFilterState;
  statusOptions: ViewingsDispatchRequest["lifecycleStatus"][];
  inspectionOptions: NonNullable<ViewingsDispatchRequest["inspectionType"]>[];
  productOptions: string[];
  onApply: (filters: InspectorFilterState) => void;
  onClear: () => void;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [stagedFilters, setStagedFilters] = useState(filters);
  const appliedFilterCount = getInspectorFilterCount(filters);
  const stagedFilterCount = getInspectorFilterCount(stagedFilters);

  useEffect(() => {
    if (open) setStagedFilters(filters);
  }, [filters, open]);

  const groups = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: "viewings.filters.status" }),
        children: statusOptions.map((status) => (
          <FilterOption
            key={status}
            checked={stagedFilters.statuses.includes(status)}
            label={getLifecycleStatusLabel(intl, status)}
            onToggle={() =>
              setStagedFilters((current) => ({
                ...current,
                statuses: toggleValue(current.statuses, status),
              }))
            }
          />
        )),
      },
      {
        title: intl.formatMessage({ id: "viewings.filters.inspection" }),
        children: inspectionOptions.map((inspectionType) => (
          <FilterOption
            key={inspectionType}
            checked={stagedFilters.inspectionTypes.includes(inspectionType)}
            label={getInspectionTypeLabel(intl, inspectionType)}
            onToggle={() =>
              setStagedFilters((current) => ({
                ...current,
                inspectionTypes: toggleValue(
                  current.inspectionTypes,
                  inspectionType,
                ),
              }))
            }
          />
        )),
      },
      {
        title: intl.formatMessage({ id: "viewings.filters.product" }),
        children: productOptions.map((product) => (
          <FilterOption
            key={product}
            checked={stagedFilters.products.includes(product)}
            label={product}
            onToggle={() =>
              setStagedFilters((current) => ({
                ...current,
                products: toggleValue(current.products, product),
              }))
            }
          />
        )),
      },
      {
        title: intl.formatMessage({ id: "viewings.filters.distance" }),
        children: DISTANCE_OPTIONS.map((distance) => (
          <FilterOption
            key={distance}
            checked={stagedFilters.distances.includes(distance)}
            label={getDistanceLabel(intl, distance)}
            onToggle={() =>
              setStagedFilters((current) => ({
                ...current,
                distances: toggleValue(current.distances, distance),
              }))
            }
          />
        )),
      },
      {
        title: intl.formatMessage({ id: "viewings.filters.alerts" }),
        children: (
          <FilterOption
            checked={stagedFilters.alerts.includes("waitingOver48h")}
            label={intl.formatMessage({
              id: "viewings.filters.waitingOver48h",
            })}
            onToggle={() =>
              setStagedFilters((current) => ({
                ...current,
                alerts: toggleValue(current.alerts, "waitingOver48h"),
              }))
            }
          />
        ),
      },
    ],
    [inspectionOptions, intl, productOptions, stagedFilters, statusOptions],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-9 gap-2 bg-muted-background"
        >
          <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
          <FormattedMessage id="viewings.filters" />
          {appliedFilterCount > 0 ? (
            <span className="rounded bg-categories-bright-orange-700 px-1.5 py-0.5 text-xs font-medium text-white">
              {appliedFilterCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-[calc(100vw-2rem)] max-w-[340px] p-0"
        data-testid="inspector-filter-popover"
      >
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <div>
            <p className="text-sm font-semibold text-primary">
              <FormattedMessage id="viewings.filters.title" />
            </p>
            <p className="text-xs text-muted-foreground">
              <FormattedMessage id="viewings.filters.description" />
            </p>
          </div>
          {stagedFilterCount > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-destructive hover:text-destructive"
              onClick={() => {
                setStagedFilters(EMPTY_INSPECTOR_FILTERS);
                onClear();
                setOpen(false);
              }}
            >
              <X className="mr-1 h-4 w-4" aria-hidden="true" />
              <FormattedMessage id="viewings.filters.clear" />
            </Button>
          ) : null}
        </div>

        <Accordion
          type="multiple"
          defaultValue={groups.map((group) => group.title)}
          className="max-h-[360px] overflow-y-auto px-2"
        >
          {groups.map((group) => (
            <AccordionItem
              key={group.title}
              value={group.title}
              className="border-border"
            >
              <AccordionTrigger className="py-3 text-sm font-normal hover:no-underline">
                <div className="flex w-full items-center justify-between pr-2">
                  <span>{group.title}</span>
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
              </AccordionTrigger>
              <AccordionContent className="space-y-1 pb-3">
                {group.children}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="flex items-center justify-end gap-2 border-t border-border p-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
          >
            <FormattedMessage id="viewings.filters.cancel" />
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              onApply(stagedFilters);
              setOpen(false);
            }}
          >
            <FormattedMessage id="viewings.filters.apply" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FilterOption({
  checked,
  label,
  onToggle,
}: {
  checked: boolean;
  label: string;
  onToggle: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      className="flex h-auto w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm text-primary hover:bg-muted-background"
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      <Checkbox
        aria-label={label}
        checked={checked}
        onCheckedChange={onToggle}
        onClick={(event) => event.stopPropagation()}
      />
      <span>{label}</span>
    </div>
  );
}
